/**
 * Preview Map Component
 *
 * Read-only map preview for the Quiz Studio sidebar.
 * Shows filtered features with visual indication of exclusions.
 * Applies auto-merge based on prompt keys.
 */

import { useMemo, useCallback } from "react";
import { BaseMap } from "@/components/shared/BaseMap";
import type { FeatureCollection, GeoJsonProperties, Geometry, Feature } from "geojson";
import type { PathOptions } from "leaflet";
import { getFeatureIdentifier } from "@/utils/geojsonLoader";
import type { FilterRule } from "@/stores/useStudioStore";
import { useProcessedCollection } from "@/hooks/useProcessedCollection";

interface PreviewMapProps {
  collection: FeatureCollection<Geometry, GeoJsonProperties>;
  filters: FilterRule[];
  excludedFeatureIds: string[];
  promptKeys: string[];
  className?: string;
}

// Style constants
const INCLUDED_STYLE: PathOptions = {
  fillColor: "#3b82f6", // Blue-500
  color: "#ffffff",
  weight: 1,
  opacity: 0.8,
  fillOpacity: 0.4,
};

const EXCLUDED_STYLE: PathOptions = {
  fillColor: "#ef4444", // Red-500
  color: "#ffffff",
  weight: 1,
  opacity: 0.6,
  fillOpacity: 0.25,
};

// Constants to avoid breaking memoization
// NOTE: Auto-merge disabled for performance on large datasets
// The small preview doesn't need perfect merged visualization
const EMPTY_EXCLUSIONS: string[] = [];
const PROCESSING_OPTIONS = { enableAutoMerge: false };

export function PreviewMap({
  collection,
  filters,
  excludedFeatureIds,
  promptKeys,
  className,
}: PreviewMapProps) {
  // Process collection: filters + auto-merge
  // Note: We DON'T remove exclusions here, we just style them differently
  // Auto-merge happens on the filtered collection before exclusion styling
  const processedCollection = useProcessedCollection(
    collection,
    filters,
    EMPTY_EXCLUSIONS, // Don't actually remove exclusions yet, just for visual styling
    promptKeys,
    PROCESSING_OPTIONS // Use constant to avoid breaking memoization
  );

  // Build a set of excluded prompt values from the original (pre-merge) collection
  // This allows us to style merged features as excluded if they match
  const excludedPromptValues = useMemo(() => {
    if (excludedFeatureIds.length === 0 || !collection || promptKeys.length === 0) {
      return new Set<string>();
    }

    // Apply filters to get the pre-merge collection
    const filteredFeatures = collection.features.filter((feature, index) => {
      // Check if this feature is excluded
      const featureId = getFeatureIdentifier(feature, index);
      return excludedFeatureIds.includes(featureId);
    });

    // Extract prompt values from excluded features
    const promptValuesSet = new Set<string>();
    filteredFeatures.forEach((feature) => {
      if (!feature.properties) return;

      // Build a string key from all prompt values (like auto-merge comparison)
      const promptValue = promptKeys
        .map((key) => {
          const value = feature.properties?.[key];
          return Array.isArray(value)
            ? JSON.stringify([...value].sort())
            : JSON.stringify(value);
        })
        .join("||");

      if (promptValue) {
        promptValuesSet.add(promptValue);
      }
    });

    return promptValuesSet;
  }, [collection, excludedFeatureIds, promptKeys]);

  // Style function - show excluded features differently
  // For merged features, check if their prompt values match any excluded features
  const getFeatureStyle = useCallback((feature: Feature<Geometry, GeoJsonProperties>): PathOptions => {
    if (!feature.properties || promptKeys.length === 0) {
      return INCLUDED_STYLE;
    }

    // Build prompt value key for this (potentially merged) feature
    const promptValue = promptKeys
      .map((key) => {
        const value = feature.properties?.[key];
        return Array.isArray(value)
          ? JSON.stringify([...value].sort())
          : JSON.stringify(value);
      })
      .join("||");

    // Check if this prompt value is in the excluded set
    const isExcluded = excludedPromptValues.has(promptValue);

    return isExcluded ? EXCLUDED_STYLE : INCLUDED_STYLE;
  }, [promptKeys, excludedPromptValues]);

  // Handle null collection
  if (!processedCollection) {
    return null;
  }

  return (
    <BaseMap
      data={processedCollection}
      className={className}
      mapHeight="220px"
      getFeatureStyle={getFeatureStyle}
      mapStyle="voyager"
      hideLabels={false}
      initialZoom={1}
    />
  );
}
